// P1 OD-SNAPSHOT — ONE rule for every farmer-data read (Adeel ruling 2026-10-06):
//   online, live read answers        → render live, write the snapshot (only a well-formed body)
//   read in flight, snapshot on hand   → render the snapshot at once (no note yet)
//   paused / failed / slower than 8 s → render the snapshot with the "showing what is saved" note
//                                       (+ "saved <when>" once it is older than 24 h)
//   no snapshot either                → an honest empty state ("nothing is saved on this phone yet")
// A spinner never lasts beyond 8 s. A bad read (error body, 401, timeout, empty) never overwrites a good snapshot.
// Keys older than 1 h are refetched on 'online' and on app resume (OutboxProvider invalidates ['snap']).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { readSnapshot, writeSnapshot, isWellFormed, type SnapshotKey, type SnapshotRecord } from '@/shared/lib/snapshot/store';

export const SNAPSHOT_SLOW_MS = 8_000;
export const SNAPSHOT_SAVED_AT_NOTE_MS = 24 * 60 * 60 * 1000;
export const SNAPSHOT_REFRESH_MS = 60 * 60 * 1000;
export const SNAP_QUERY_PREFIX = 'snap';

export type SnapshotSource = 'live' | 'snapshot' | 'none' | 'loading';

export interface SnapshotQueryOptions<T> {
  farmerId: string | null | undefined;
  /** Extra key parts (e.g. the active cycle id) so different filters keep different live caches; the snapshot is per key only. */
  keyExtra?: unknown[];
  enabled?: boolean;
  /** Extra shape check on the live body before it may become the snapshot. */
  validate?: (data: T) => boolean;
  staleTime?: number;
  refetchOnMount?: boolean | 'always';
  /** Called after each live success that was written through (P2: the Photos screen precaches thumbnails from it). */
  onLive?: (data: T) => void;
  /** Tests: shorten the slow-read budget. */
  slowMs?: number;
  nowMs?: () => number;
}

export interface SnapshotQueryResult<T> {
  data: T | undefined;
  source: SnapshotSource;
  /** Bounded: true only while a live read is still within the slow budget and nothing is on hand. */
  isLoading: boolean;
  isError: boolean;
  /** The screen should show the no-signal note (data on screen is the saved copy, or nothing could be read). */
  noSignal: boolean;
  savedAt: string | null;
  /** Older than 24 h → the note carries "saved <when>". */
  showSavedAt: boolean;
  refetch: () => void;
}

export function snapQueryKey(key: SnapshotKey, farmerId: string | null | undefined, extra: unknown[] = []): QueryKey {
  return [SNAP_QUERY_PREFIX, key, farmerId ?? 'anon', ...extra];
}

export function useSnapshotQuery<T>(key: SnapshotKey, liveFn: () => Promise<T>, opts: SnapshotQueryOptions<T>): SnapshotQueryResult<T> {
  const { farmerId } = opts;
  const enabled = !!farmerId && (opts.enabled ?? true);
  const now = opts.nowMs ?? Date.now;
  const slowMs = opts.slowMs ?? SNAPSHOT_SLOW_MS;

  const q = useQuery<T>({
    queryKey: snapQueryKey(key, farmerId, opts.keyExtra),
    queryFn: async () => {
      const body = await liveFn();
      if (!isWellFormed(body) || (opts.validate && !opts.validate(body))) throw new Error(`snapshot: bad read for ${key}`);
      if (farmerId) {
        void writeSnapshot(farmerId, key, body, now());
        try {
          opts.onLive?.(body);
        } catch {
          /* best effort — never fails the read */
        }
      }
      return body;
    },
    enabled,
    staleTime: opts.staleTime ?? SNAPSHOT_REFRESH_MS,
    refetchOnMount: opts.refetchOnMount ?? 'always',
    retry: 1,
  });

  // The saved copy: undefined = not looked yet, null = none.
  const [snap, setSnap] = useState<SnapshotRecord<T> | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    setSnap(undefined);
    if (!farmerId) {
      setSnap(null);
      return;
    }
    readSnapshot<T>(farmerId, key).then((rec) => alive && setSnap(rec));
    return () => {
      alive = false;
    };
  }, [farmerId, key]);
  // A live success is the newest copy; keep the local record in step without another IndexedDB round trip.
  useEffect(() => {
    if (q.isSuccess && q.data !== undefined && farmerId) setSnap({ schema: 1, farmer_id: farmerId, key, written_at: new Date(now()).toISOString(), data: q.data });
  }, [q.isSuccess, q.data, farmerId, key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Slow-read budget: nothing on hand and still fetching after `slowMs` → fall back to the snapshot.
  const [slow, setSlow] = useState(false);
  const fetching = q.fetchStatus === 'fetching' && q.data === undefined;
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (fetching) {
      timer.current = window.setTimeout(() => setSlow(true), slowMs);
      return () => {
        if (timer.current) window.clearTimeout(timer.current);
      };
    }
    setSlow(false);
    return undefined;
  }, [fetching, slowMs, q.dataUpdatedAt]);

  return useMemo<SnapshotQueryResult<T>>(() => {
    const cannotRead = q.fetchStatus === 'paused' || q.isError || slow || !enabled;
    const liveDown = q.fetchStatus === 'paused' || q.isError;
    if (q.data !== undefined) {
      return { data: q.data, source: 'live', isLoading: false, isError: q.isError, noSignal: liveDown, savedAt: snap?.written_at ?? null, showSavedAt: false, refetch: () => void q.refetch() };
    }
    if (cannotRead) {
      if (snap === undefined) return { data: undefined, source: 'loading', isLoading: true, isError: false, noSignal: true, savedAt: null, showSavedAt: false, refetch: () => void q.refetch() };
      if (snap) {
        const age = now() - Date.parse(snap.written_at);
        return { data: snap.data, source: 'snapshot', isLoading: false, isError: q.isError, noSignal: true, savedAt: snap.written_at, showSavedAt: age > SNAPSHOT_SAVED_AT_NOTE_MS, refetch: () => void q.refetch() };
      }
      return { data: undefined, source: 'none', isLoading: false, isError: q.isError, noSignal: true, savedAt: null, showSavedAt: false, refetch: () => void q.refetch() };
    }
    // Read in flight, saved copy on hand → the saved copy NOW (the farmer always sees the last saved data); the note only
    // once the read has actually failed, paused or run past the budget. A dead radio can take seconds to give up —
    // the screen never waits for it.
    if (snap) {
      const age = now() - Date.parse(snap.written_at);
      return { data: snap.data, source: 'snapshot', isLoading: false, isError: false, noSignal: false, savedAt: snap.written_at, showSavedAt: age > SNAPSHOT_SAVED_AT_NOTE_MS, refetch: () => void q.refetch() };
    }
    return { data: undefined, source: 'loading', isLoading: true, isError: false, noSignal: false, savedAt: null, showSavedAt: false, refetch: () => void q.refetch() };
  }, [q.data, q.fetchStatus, q.isError, slow, enabled, snap, now, q.refetch]); // eslint-disable-line react-hooks/exhaustive-deps
}
